import React, { useEffect, useState } from "react";
import { useCart } from "../contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CreditCard, Loader2, ArrowLeft, Zap, Tag, X, Apple } from "lucide-react";

const detectLang = (s) => /[\u0370-\u03ff]/.test(s) ? "el" : "en";

// Detect platform to pick a sensible default payment method
const detectDefaultPayment = () => {
    if (typeof navigator === "undefined") return "card";
    const ua = navigator.userAgent || "";
    if (/iPhone|iPad|iPod|Mac/.test(ua)) return "apple_pay";
    if (/Android/i.test(ua)) return "google_pay";
    return "card";
};

// Note: Stripe Checkout (hosted page) automatically supports Apple Pay and Google Pay
// when payment_method_types includes "card". We just pass the user's preference for UI display.
export const Checkout = () => {
    const { items, subtotal, clear, consumePendingCoupon, pendingCoupon } = useCart();
    const navigate = useNavigate();
    const [form, setForm] = useState({ customer_name: "", phone: "", address: "" });
    const [discount, setDiscount] = useState(0);
    const [appliedCode, setAppliedCode] = useState("");
    const [promoInput, setPromoInput] = useState("");
    const [promoError, setPromoError] = useState("");
    const [paymentMethod, setPaymentMethod] = useState(() => detectDefaultPayment());
    const [loading, setLoading] = useState(false);

    const lang = detectLang(items.map((i) => i.name).join(" ") + form.customer_name);
    const deliveryFee = subtotal >= 25 || subtotal === 0 ? 0 : 2.5;
    const total = Math.max(0, subtotal - discount + deliveryFee);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    // Auto-apply pending coupon (e.g. from spin wheel)
    useEffect(() => {
        if (pendingCoupon && subtotal > 0 && !appliedCode) {
            const code = consumePendingCoupon();
            (async () => {
                try {
                    const { data } = await api.post("/coupons/validate", { code, subtotal });
                    setDiscount(data.discount);
                    setAppliedCode(data.code);
                    toast.success(`✓ Κουπόνι "${data.code}" εφαρμόστηκε`);
                } catch {}
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingCoupon, subtotal]);

    const applyPromo = async () => {
        const code = promoInput.trim().toUpperCase();
        if (!code) return;
        setPromoError("");
        try {
            const { data } = await api.post("/coupons/validate", { code, subtotal });
            setDiscount(data.discount);
            setAppliedCode(data.code);
            setPromoInput("");
            toast.success(`✓ Κουπόνι "${data.code}" εφαρμόστηκε`);
        } catch (err) {
            setPromoError(err.response?.data?.detail || "Μη έγκυρο κουπόνι");
        }
    };

    const removePromo = () => { setDiscount(0); setAppliedCode(""); };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (items.length === 0) return;
        setLoading(true);
        try {
            const orderPayload = {
                items: items.map((i) => ({ item_id: i.id, quantity: i.qty })),
                customer_name: form.customer_name,
                phone: form.phone,
                address: form.address,
                notes: "",
                payment_method: "card", // ALL digital methods route through Stripe
                coupon_code: appliedCode || null,
            };
            const { data: order } = await api.post("/orders", orderPayload);

            // ALL payment methods → Stripe Checkout (which supports Apple Pay & Google Pay automatically)
            const { data: ck } = await api.post("/payments/checkout", {
                order_id: order.id,
                origin_url: window.location.origin,
            });
            clear();
            window.location.href = ck.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || (lang === "el" ? "Σφάλμα" : "Error"));
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="cyber-bg cyber-grid scanlines min-h-screen grid place-items-center px-4 text-white" data-testid="checkout-empty">
                <div className="text-center max-w-sm">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40">// no_items</p>
                    <h1 className="font-heading text-4xl mt-3" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Cart is empty.</h1>
                    <button onClick={() => navigate("/")} className="gold-cta mt-8 h-12 px-8 rounded-full font-mono uppercase tracking-widest text-xs font-bold inline-flex items-center gap-2" data-testid="empty-go-home">
                        <ArrowLeft size={14} /> Πίσω στο app
                    </button>
                </div>
            </div>
        );
    }

    const ctaLabel = paymentMethod === "apple_pay" ? "Πληρωμή με Apple Pay"
        : paymentMethod === "google_pay" ? "Πληρωμή με Google Pay"
        : "Πληρωμή με κάρτα";

    return (
        <div className="cyber-bg cyber-grid scanlines min-h-screen text-white pb-32" data-testid="checkout-page">
            <div className="mx-auto max-w-lg px-4 pt-4">
                <button type="button" onClick={() => navigate(-1)} data-testid="ck-back"
                    className="inline-flex items-center gap-1.5 text-white/60 hover:text-[var(--gold-light)] text-xs font-mono uppercase tracking-widest mb-3 transition">
                    <ArrowLeft size={14} /> Πίσω
                </button>

                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--gold-light)]">// checkout</p>
                <h1 className="font-heading text-4xl mt-1 mb-5" style={{ fontFamily: '"Cormorant Garamond", serif' }}>Πληρωμή</h1>

                <form onSubmit={submit} className="space-y-5">
                    {/* Customer details */}
                    <section className="space-y-3">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">// στοιχεία</p>
                        <input required data-testid="ck-name" placeholder="Ονοματεπώνυμο" value={form.customer_name} onChange={set("customer_name")}
                            className="w-full glass-gold border-2 border-white/15 px-5 h-14 text-base rounded-xl focus:outline-none focus:border-[var(--gold)] transition placeholder:text-white/40" />
                        <input required type="tel" data-testid="ck-phone" placeholder="Τηλέφωνο" value={form.phone} onChange={set("phone")}
                            className="w-full glass-gold border-2 border-white/15 px-5 h-14 text-base rounded-xl focus:outline-none focus:border-[var(--gold)] transition placeholder:text-white/40" />
                        <input required data-testid="ck-address" placeholder="Διεύθυνση" value={form.address} onChange={set("address")}
                            className="w-full glass-gold border-2 border-white/15 px-5 h-14 text-base rounded-xl focus:outline-none focus:border-[var(--gold)] transition placeholder:text-white/40" />
                    </section>

                    {/* Payment Methods */}
                    <section className="space-y-3">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">// τρόπος πληρωμής</p>
                        <div className="grid grid-cols-3 gap-2.5">
                            <PaymentBtn id="apple_pay"  active={paymentMethod === "apple_pay"}  onClick={() => setPaymentMethod("apple_pay")}
                                icon={<Apple size={26} className="fill-current" />} label="Apple Pay" />
                            <PaymentBtn id="google_pay" active={paymentMethod === "google_pay"} onClick={() => setPaymentMethod("google_pay")}
                                icon={<GPayIcon />} label="Google Pay" />
                            <PaymentBtn id="card"       active={paymentMethod === "card"}       onClick={() => setPaymentMethod("card")}
                                icon={<CreditCard size={26} />} label="Κάρτα" />
                        </div>
                        <p className="text-[10px] text-white/40 text-center font-mono">
                            🔒 Ασφαλείς ψηφιακές πληρωμές μέσω Stripe
                        </p>
                    </section>

                    {/* Promo code */}
                    <section>
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">// κουπόνι</p>
                        {appliedCode ? (
                            <div className="flex items-center gap-2 p-3 glass-gold border border-[var(--gold)]/40 rounded-xl">
                                <Tag size={14} className="text-[var(--gold-light)]" />
                                <span className="flex-1 text-sm">
                                    <span className="font-bold text-[var(--gold-light)]">{appliedCode}</span>
                                    <span className="text-white/60"> · -€{discount.toFixed(2)}</span>
                                </span>
                                <button type="button" onClick={removePromo} data-testid="ck-promo-remove" className="text-white/40 hover:text-white">
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input data-testid="ck-promo-input" placeholder="Π.χ. WELCOME10" value={promoInput}
                                    onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(""); }}
                                    className="flex-1 glass-gold border-2 border-white/15 px-4 h-12 rounded-xl text-sm focus:outline-none focus:border-[var(--gold)] placeholder:text-white/40" />
                                <button type="button" onClick={applyPromo} data-testid="ck-promo-apply"
                                    className="px-5 h-12 rounded-xl border-2 border-[var(--gold)]/40 hover:border-[var(--gold)] hover:bg-[var(--gold)]/10 font-mono uppercase tracking-widest text-[10px] font-bold text-[var(--gold-light)] transition">
                                    Εφαρμογή
                                </button>
                            </div>
                        )}
                        {promoError && <p className="text-xs text-[var(--neon-red)] mt-1.5" data-testid="ck-promo-error">{promoError}</p>}
                    </section>

                    {/* Order Summary */}
                    <section className="glass-gold border border-white/10 rounded-xl p-4 space-y-2.5" data-testid="ck-summary">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">// παραγγελία</p>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                            {items.map((i) => (
                                <div key={i.id} className="flex justify-between text-sm">
                                    <span className="text-white/80 truncate mr-2">{i.qty}× {i.name}</span>
                                    <span className="font-mono font-bold shrink-0">€{(i.price * i.qty).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-white/10" />
                        <div className="flex justify-between text-sm font-mono">
                            <span className="text-white/60">Υποσύνολο</span>
                            <span>€{subtotal.toFixed(2)}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between text-sm gold-text font-mono" data-testid="ck-discount-row">
                                <span>Έκπτωση ({appliedCode})</span>
                                <span>-€{discount.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-sm font-mono">
                            <span className="text-white/60">Μεταφορικά</span>
                            <span>{deliveryFee === 0 ? "ΔΩΡΕΑΝ" : `€${deliveryFee.toFixed(2)}`}</span>
                        </div>
                        <div className="border-t border-white/10" />
                        <div className="flex justify-between text-2xl font-heading font-black">
                            <span>Σύνολο</span>
                            <span data-testid="ck-total" className="gold-text font-mono">€{total.toFixed(2)}</span>
                        </div>
                    </section>
                </form>
            </div>

            {/* Sticky bottom submit */}
            <div className="fixed bottom-0 left-0 right-0 z-30 glass-gold border-t border-[var(--gold)]/30 p-4">
                <div className="mx-auto max-w-lg">
                    <button onClick={submit} disabled={loading} type="button" data-testid="ck-submit"
                        className="gold-cta w-full h-14 rounded-full font-mono uppercase tracking-widest text-sm font-bold flex items-center justify-center gap-2">
                        {loading ? <Loader2 className="animate-spin" size={18} /> : <Zap size={16} strokeWidth={3} />}
                        {ctaLabel} — €{total.toFixed(2)}
                    </button>
                </div>
            </div>
        </div>
    );
};

const PaymentBtn = ({ id, active, onClick, icon, label }) => (
    <button type="button" data-testid={`pm-${id}`} onClick={onClick}
        className={`flex flex-col items-center justify-center gap-1.5 h-24 rounded-2xl transition active:scale-95 ${
            active
                ? "border-2 border-[var(--gold)] bg-[var(--gold)]/10 shadow-[0_0_24px_rgba(244,179,57,0.4)] text-[var(--gold-light)]"
                : "border-2 border-white/12 hover:border-white/30 glass-gold text-white/75"
        }`}>
        {icon}
        <span className="font-mono uppercase tracking-widest text-[9px] font-bold">{label}</span>
    </button>
);

// Inline Google Pay icon (multicolor G)
const GPayIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21.6 12.227c0-.682-.061-1.336-.176-1.964h-9.42v3.711h5.385a4.611 4.611 0 0 1-2 3.025v2.498h3.234c1.892-1.745 2.977-4.318 2.977-7.27z" fill="#4285F4"/>
        <path d="M12.004 22c2.7 0 4.965-.892 6.62-2.42l-3.235-2.498c-.896.6-2.04.957-3.385.957-2.604 0-4.81-1.76-5.6-4.122H3.063v2.578A9.996 9.996 0 0 0 12.004 22z" fill="#34A853"/>
        <path d="M6.404 13.917a6.013 6.013 0 0 1-.318-1.917c0-.665.117-1.31.318-1.917V7.505H3.063a9.997 9.997 0 0 0 0 8.99l3.34-2.578z" fill="#FBBC04"/>
        <path d="M12.004 5.962c1.47 0 2.785.505 3.823 1.498l2.864-2.864C16.965 2.99 14.7 2 12.004 2A9.997 9.997 0 0 0 3.063 7.505l3.341 2.578c.79-2.362 2.996-4.121 5.6-4.121z" fill="#EA4335"/>
    </svg>
);
